import { normalizeWorkspaceContextId } from '@/utils/workspaceContext';

export const DEFAULT_TOOL_ITERATION_LIMIT = 6;
export const MIN_TOOL_ITERATION_LIMIT = 5;
export const MAX_TOOL_ITERATION_LIMIT = 50;

export const EMPTY_WORKSPACE_ACCESS_SETTINGS = Object.freeze({
    activeToolIds: null,
    activeServerIds: null,
    toolIterationLimit: null,
});

const normalizeIdList = (value) => {
    if (!Array.isArray(value)) {
        return null;
    }

    const normalized = value
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean);

    return Array.from(new Set(normalized));
};

const normalizeToolIterationLimit = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return null;
    }

    return Math.min(
        MAX_TOOL_ITERATION_LIMIT,
        Math.max(MIN_TOOL_ITERATION_LIMIT, Math.floor(numericValue)),
    );
};

export const normalizeWorkspaceAccessSettings = (settings) => {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return { ...EMPTY_WORKSPACE_ACCESS_SETTINGS };
    }

    return {
        activeToolIds: normalizeIdList(settings.activeToolIds),
        activeServerIds: normalizeIdList(settings.activeServerIds),
        toolIterationLimit: normalizeToolIterationLimit(settings.toolIterationLimit),
    };
};

export const resolveWorkspaceAccessScopeId = (conversationOrWorkspaceId) => {
    if (typeof conversationOrWorkspaceId === 'string') {
        return normalizeWorkspaceContextId(conversationOrWorkspaceId);
    }

    return normalizeWorkspaceContextId(conversationOrWorkspaceId?.workspaceContextId);
};

export const findWorkspaceById = (workspaceState, workspaceId) => {
    const normalizedWorkspaceId = resolveWorkspaceAccessScopeId(workspaceId);
    if (!normalizedWorkspaceId) {
        return null;
    }

    if (workspaceState?.activeWorkspaceDetail?.id === normalizedWorkspaceId) {
        return workspaceState.activeWorkspaceDetail;
    }

    return workspaceState?.workspaces?.find((workspace) => workspace.id === normalizedWorkspaceId) || null;
};

export const getWorkspaceForConversation = (workspaceState, conversation) => {
    return findWorkspaceById(workspaceState, resolveWorkspaceAccessScopeId(conversation));
};

export const getEffectiveActiveToolIds = ({ conversation, workspace, fallbackActiveToolIds = [] }) => {
    const workspaceAccessSettings = normalizeWorkspaceAccessSettings(workspace?.workspaceAccessSettings);
    if (resolveWorkspaceAccessScopeId(conversation) && workspaceAccessSettings.activeToolIds !== null) {
        return workspaceAccessSettings.activeToolIds;
    }

    return Array.isArray(conversation?.toolSettings?.activeToolIds)
        ? conversation.toolSettings.activeToolIds
        : fallbackActiveToolIds;
};

export const getEffectiveActiveServerIds = ({ conversation, workspace, fallbackActiveServerIds = [] }) => {
    const workspaceAccessSettings = normalizeWorkspaceAccessSettings(workspace?.workspaceAccessSettings);
    if (resolveWorkspaceAccessScopeId(conversation) && workspaceAccessSettings.activeServerIds !== null) {
        return workspaceAccessSettings.activeServerIds;
    }

    return Array.isArray(conversation?.mcpSettings?.activeServerIds)
        ? conversation.mcpSettings.activeServerIds
        : fallbackActiveServerIds;
};

export const getEffectiveToolIterationLimit = ({
    conversation,
    workspace,
    fallbackToolIterationLimit = DEFAULT_TOOL_ITERATION_LIMIT,
}) => {
    const workspaceAccessSettings = normalizeWorkspaceAccessSettings(workspace?.workspaceAccessSettings);
    if (resolveWorkspaceAccessScopeId(conversation) && workspaceAccessSettings.toolIterationLimit !== null) {
        return workspaceAccessSettings.toolIterationLimit;
    }

    return normalizeToolIterationLimit(conversation?.toolSettings?.toolIterationLimit)
        ?? fallbackToolIterationLimit;
};

export const mergeWorkspaceAccessSettings = (workspace, partialSettings) => {
    const currentSettings = normalizeWorkspaceAccessSettings(workspace?.workspaceAccessSettings);

    return {
        activeToolIds: Object.prototype.hasOwnProperty.call(partialSettings, 'activeToolIds')
            ? partialSettings.activeToolIds
            : currentSettings.activeToolIds,
        activeServerIds: Object.prototype.hasOwnProperty.call(partialSettings, 'activeServerIds')
            ? partialSettings.activeServerIds
            : currentSettings.activeServerIds,
        toolIterationLimit: Object.prototype.hasOwnProperty.call(partialSettings, 'toolIterationLimit')
            ? partialSettings.toolIterationLimit
            : currentSettings.toolIterationLimit,
    };
};